import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { channelTypeService } from '@/services/channel-types';
import { supabase } from '@/lib/supabase';
import { BusinessError } from '@/features/channels/utils/errors';
import type { 
  CreateChannelTypeInput, 
  UpdateChannelTypeInput, 
  DeleteChannelTypeInput 
} from '@/services/channel-types';

// Helper function for error handling
const handleDatabaseError = (error: any) => {
  console.error('Database error:', error);
  
  switch (error.code) {
    case '42703':
      throw new BusinessError('System configuration error. Please contact support.');
    case '406':
      throw new BusinessError('Invalid version configuration');
    case '23505':
      if (error.message.includes('unique_provider_sequence_type')) {
        throw new BusinessError('Sequential provider can only have one channel type');
      }
      if (error.message.includes('unique_provider_version_type_name')) {
        throw new BusinessError('Channel type name must be unique within version');
      }
      throw new BusinessError('Duplicate entry detected');
    default:
      throw error;
  }
};

// Version validation helper
const validateVersion = async (providerId: number, versionId: number): Promise<{ status: string }> => {
  const { data, error } = await supabase
    .from('provider_versions')
    .select('status')
    .eq('provider_id', providerId)
    .eq('id', versionId)
    .single();

  if (error) {
    handleDatabaseError(error);
  }

  if (!data) {
    throw new BusinessError('Version not found');
  }

  return data;
};

// Add these validation helper functions
const validateDisplayOrderUniqueness = async (
  providerId: number,
  versionId: number,
  display_order: number,
  excludeTypeId?: number
) => {
  const { data, error } = await supabase
    .from('channel_types')
    .select('id')
    .eq('provider_id', providerId)
    .eq('version_id', versionId)
    .eq('display_order', display_order)
    .neq('id', excludeTypeId || 0);

  if (error) handleDatabaseError(error);
  
  if (data && data.length > 0) {
    throw new BusinessError('Display order must be unique within provider version');
  }
};

const validateDefaultTypeRequirements = async (
  providerId: number,
  versionId: number,
  is_default: boolean,
  currentTypeId?: number
) => {
  if (!is_default) {
    const { data, error } = await supabase
      .from('channel_types')
      .select('id')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('is_default', true)
      .neq('id', currentTypeId || 0);

    if (error) handleDatabaseError(error);

    if (!data || data.length === 0) {
      throw new BusinessError('At least one default channel type is required');
    }
  }
};

const validateSequentialTypeConstraints = async (
  providerId: number,
  is_sequence: boolean
) => {
  if (!is_sequence) return;

  const { data: provider, error: providerError } = await supabase
    .from('providers')
    .select('has_sequence_numbers')
    .eq('id', providerId)
    .single();

  if (providerError) handleDatabaseError(providerError);

  if (!provider?.has_sequence_numbers) {
    throw new BusinessError('Sequential types can only be added to providers with sequence number support');
  }

  const { data: existingTypes, error: typesError } = await supabase
    .from('channel_types')
    .select('id')
    .eq('provider_id', providerId)
    .eq('is_sequence', true);

  if (typesError) handleDatabaseError(typesError);

  if (existingTypes && existingTypes.length > 0) {
    throw new BusinessError('Only one sequential channel type is allowed per provider');
  }
};

// Get all channel types with refresh functionality
export function useChannelTypes(providerId?: number, versionId?: number) {
  return useQuery({
    queryKey: ['channel-types', providerId, versionId],
    queryFn: async () => {
      if (!providerId || !versionId) {
        throw new BusinessError('Provider ID and Version ID are required');
      }

      try {
        // Validate version first
        await validateVersion(providerId, versionId);

        // Get provider details
        const { data: provider, error: providerError } = await supabase
          .from('providers')
          .select('*')
          .eq('id', providerId)
          .single();

        if (providerError) {
          handleDatabaseError(providerError);
        }

        if (provider && provider.has_sequence_numbers) {
          // For sequential providers, fetch the sequence type for specific version
          const { data: sequenceType, error: sequenceError } = await supabase
            .from('channel_types')
            .select('*')
            .eq('provider_id', providerId)
            .eq('version_id', versionId)
            .eq('is_sequence', true)
            .single();

          if (sequenceError && sequenceError.code !== 'PGRST116') {
            handleDatabaseError(sequenceError);
          }

          return sequenceType ? [sequenceType] : [];
        } else {
          // For non-sequential providers, fetch version-specific types
          const { data, error } = await supabase
            .from('channel_types')
            .select('*')
            .eq('provider_id', providerId)
            .eq('version_id', versionId)
            .order('display_order');

          if (error) {
            handleDatabaseError(error);
          }

          return data || [];
        }
      } catch (error) {
        handleDatabaseError(error);
      }
    },
    enabled: !!providerId && !!versionId,
    retry: 1,
    staleTime: 30000,
  });
}

// Get single channel type
export function useChannelType(typeId: number | null) {
  return useQuery({
    queryKey: ['channel-type', typeId],
    queryFn: () => typeId ? channelTypeService.getChannelType(typeId) : null,
    enabled: !!typeId
  });
}

// Create channel type with validation
export function useCreateChannelType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateChannelTypeInput) => {
      try {
        await validateVersion(variables.providerId, variables.versionId);

        await validateDisplayOrderUniqueness(
          variables.providerId,
          variables.versionId,
          variables.display_order
        );

        await validateDefaultTypeRequirements(
          variables.providerId,
          variables.versionId,
          variables.is_default ?? false
        );

        await validateSequentialTypeConstraints(
          variables.providerId,
          variables.is_sequence ?? false
        );

        return await channelTypeService.createChannelType(variables);
      } catch (error) {
        handleDatabaseError(error);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['channel-types', variables.providerId] 
      });
      toast.success('The channel type has been created successfully.');
    },
    onError: (error) => {
      toast.error(error instanceof BusinessError ? error.message : 'Failed to create channel type');
    }
  });
}

// Update channel type with validation
export function useUpdateChannelType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateChannelTypeInput) => {
      console.log('Mutation: Starting update with variables:', variables);
      
      try {
        // Validate version first
        await validateVersion(variables.providerId, variables.versionId);
        console.log('Mutation: Version validated');

        if (variables.is_default !== undefined) {
          await validateDefaultTypeRequirements(
            variables.providerId,
            variables.versionId,
            variables.is_default,
            variables.typeId
          );
          console.log('Mutation: Default type requirements validated');
        }

        const result = await channelTypeService.updateChannelType(variables);
        console.log('Mutation: Update successful:', result);
        return result;
      } catch (error) {
        console.error('Mutation: Update failed:', error);
        handleDatabaseError(error);
        throw error;
      }
    },
    onMutate: async (variables) => {
      console.log('Mutation: Starting optimistic update');
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: ['channel-types', variables.providerId, variables.versionId] 
      });

      // Snapshot the previous value
      const previousTypes = queryClient.getQueryData([
        'channel-types',
        variables.providerId,
        variables.versionId
      ]);

      console.log('Mutation: Previous data:', previousTypes);
      return { previousTypes };
    },
    onSuccess: (data, variables) => {
      console.log('Mutation: Success callback with data:', data);
      
      // Update queries
      queryClient.invalidateQueries({
        queryKey: ['channel-types', variables.providerId, variables.versionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['channel-type', variables.typeId]
      });

      toast.success('Channel type updated successfully');
    },
    onError: (error, variables, context) => {
      console.error('Mutation: Error callback:', error);
      
      // Rollback to previous value
      if (context?.previousTypes) {
        queryClient.setQueryData(
          ['channel-types', variables.providerId, variables.versionId],
          context.previousTypes
        );
      }

      toast.error(error instanceof Error ? error.message : 'Failed to update channel type');
    },
    onSettled: (_error, _data, variables,) => {
      console.log('Mutation: Settled callback');
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: ['channel-types', variables.providerId, variables.versionId]
      });
    }
  });
}

// Delete channel type with validation
export function useDeleteChannelType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DeleteChannelTypeInput) => {
      try {
        await validateVersion(variables.providerId, variables.versionId);

        const { data: type, error: typeError } = await supabase
          .from('channel_types')
          .select('*, provider:providers(has_sequence_numbers)')
          .eq('id', variables.typeId)
          .single();

        if (typeError) {
          handleDatabaseError(typeError);
        }

        if (type?.is_sequence && type.provider?.has_sequence_numbers) {
          throw new BusinessError('Cannot delete sequence channel type');
        }

        return await channelTypeService.deleteChannelType(variables);
      } catch (error) {
        handleDatabaseError(error);
      }
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ 
        queryKey: ['channel-types', variables.providerId] 
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['channel-types', variables.providerId] 
      });
      toast.success('Channel type deleted successfully');
    },
    onError: (error: any) => {
      const message = error instanceof Error ? error.message : 'Failed to delete channel type';
      
      toast.error(message.includes('being used by channels')
        ? 'This type is being used by one or more channels. Please remove all channel associations first.'
        : message);
    }
  });
} 